import React, { FC, useMemo } from 'react'
import { type ArticleData } from '../server/utils/getArticlesData'
import { ReactSVG } from 'react-svg'
import Skeleton from 'react-loading-skeleton'

import Chip from './Chip'
import Card from './Card'
import Dropdown from './Dropdown'
import BtnShare from './BtnShare'
import DropdownShare from './DropdownShare'
import Participants from './Participants'
import Favorite from './Favorite'
import { favoriteHandler } from '../utils/favoriteHandler'
import { axiosInstance } from '../hooks/useAxios'
import { joinEvent } from '../api/joinEvent'
import { leaveEvent } from '../api/leaveEvent'
import Participation from './Participation'
import { useMediaQuery } from 'react-responsive'
import classNames from 'classnames'
import { Link } from 'react-router-dom';

interface Props extends ArticleData {
  loading: boolean
}

const EventDetailCard: FC<Props> = ({
  id,
  loading,
  title,
  tags,
  informationItems,
  shareLinks,
  detailPicture,
  participants,
  favorite,
  participation,
  informationTags,
  hasParticipation
}) => {
  const participationHandler = async (value: boolean, id: string) => {
    if (value) {
      await axiosInstance(joinEvent('events', id))
    } else {
      await axiosInstance(leaveEvent('events', id))
    }
  }
  const isTablet = useMediaQuery({
    query: '(max-width: 1023px)'
  })

  const _tags = useMemo(() => {
    if (isTablet) return (tags ?? []).concat(informationTags ?? [])

    return informationTags ?? []
  }, [isTablet, tags, informationTags])

  return (
    <>
      {loading ? (
        <Card className="event-detail-card">
          <div className="event-detail-card__main">
            <div className="event-detail-card__top">
              <Skeleton className="mb-6" height="4rem" />
              <div className="event-detail-card__wrapper">
                <div className="community-detail-card__tags">
                  {Array(3)
                    .fill(1)
                    .map((_, index) => (
                      <Skeleton key={index} height="3rem" width="10rem" />
                    ))}
                </div>
              </div>
            </div>
            <div className="event-detail-card__info">
              <Skeleton count={3} />
            </div>
            <div className="event-detail-card__actions">
              <Skeleton width="20rem" height="4.5rem" />
            </div>
          </div>
          <div className="event-detail-card__sidebar">
            <Skeleton className="mb-6" height="20rem" />
            <Skeleton height="5rem" />
          </div>
        </Card>
      ) : (
        <Card className="event-detail-card">
          <div className="event-detail-card__main">
            <div className="event-detail-card__top">
              {tags && tags.length ? (
                <div className="event-detail-card__tags _top">
                  {tags.map((tag, index) => (
                    <Chip key={index} color={tag.color}>
                      <>
                        {tag.icon && (
                          <ReactSVG
                            className={'svg-icon'}
                            src={tag.icon}
                            wrapper={'span'}
                          ></ReactSVG>
                        )}
                        <span>{tag.label}</span>
                      </>
                    </Chip>
                  ))}
                </div>
              ) : null}
              <h1
                className={classNames([
                  'event-detail-card__title',
                  { 'text-h2': title.length > 50 }
                ])}
              >
                {title}
              </h1>
              <div className="event-detail-card__wrapper">
                {_tags.length ? (
                  <div className="event-detail-card__tags">
                    {_tags.map((tag, index) => (
                      <Chip key={index} color={tag.color}>
                        <>
                          {tag.icon && (
                            <ReactSVG
                              className={'svg-icon'}
                              src={tag.icon}
                              wrapper={'span'}
                            ></ReactSVG>
                          )}
                          <span>{tag.label}</span>
                        </>
                      </Chip>
                    ))}
                  </div>
                ) : null}
                <Dropdown
                  className="event-detail-card__share-mobile"
                  trigger={(open) => (
                    <BtnShare onClick={open}>Поделиться</BtnShare>
                  )}
                  render={(close) => (
                    <DropdownShare items={shareLinks} onClick={close} />
                  )}
                />
              </div>
            </div>

            {informationItems && (
              <ul className="event-detail-card__info">
                {informationItems.map(({ icon, text }, index) => (
                  <li key={index}>
                    <ReactSVG src={`/i/svg/${icon}.svg`} wrapper="span" />
                    {text}
                  </li>
                ))}
              </ul>
            )}
            {hasParticipation ? (
              <div className="event-detail-card__actions">
                <Participation
                  value={!!participation}
                  handler={(value) => {
                    return participationHandler(value, id as string)
                  }}
                ></Participation>
              </div>
            ) : null}
          </div>
          <div className="event-detail-card__sidebar">
            <div className="event-detail-card__image">
              <Favorite
                value={!!favorite}
                className="community-detail-card__bookmark"
                handler={(value) => {
                  return favoriteHandler(value, id as string, 'events')
                }}
              ></Favorite>
              <img src={detailPicture} alt="image" />
            </div>
            <Participants
              className="event-detail-card__participants"
              title={'Заявки на участие'}
              items={participants?.items.length ? participants.items : []}
              count={participants?.count ? participants.count : 0}

              link={participants?.items.length && participants.canViewParticipants
                ? () =>  <Link to={`/events/${id}/participants`} className={'participants__link'}>Смотреть</Link>
                : () => null
              }
            />
            <Dropdown
              className="event-detail-card__share"
              trigger={(open) => <BtnShare onClick={open}>Поделиться</BtnShare>}
              render={(close) => (
                <DropdownShare items={shareLinks} onClick={close} />
              )}
            />
          </div>
        </Card>
      )}
    </>
  )
}

export default EventDetailCard
